import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { SITE } from "@/data/site";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputCls =
  "w-full rounded-[10px] border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-tertiary focus:outline-none focus:border-brand-royal focus:ring-2 focus:ring-brand-royal/20 transition-colors";
const labelCls = "block text-sm font-semibold text-brand-deep mb-2";

export default function ContactPage() {
  const [data, setData] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    const e = {};
    if (!data.name.trim()) e.name = "Votre nom est requis";
    if (!EMAIL_RE.test(data.email.trim())) e.email = "Adresse email invalide";
    if (!data.message.trim()) e.message = "Écrivez votre message";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name: data.name.trim(), email: data.email.trim(), phone: data.phone.trim(), message: data.message.trim() });
      setSent(true);
    } catch {
      setErrors({ submit: "Une erreur est survenue. Réessayez ou appelez-nous directement." });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact | HRC Rénovation Coulounieix-Chamiers"
        description="Contactez HRC Rénovation : 06 41 96 83 46, hrcrenovation24@gmail.com. Coulounieix-Chamiers, Dordogne. Devis gratuit 48h."
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://hrcrenovation.fr/" },
            { "@type": "ListItem", position: 2, name: "Contact" },
          ],
        }]}
      />
      <section className="pt-[120px] pb-24 bg-surface-canvas min-h-screen">
        <div className="max-w-site mx-auto px-5 lg:px-6">
          <Reveal>
            <p className="micro-label mb-4">Contact</p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-brand-deep max-w-[700px]" style={{ letterSpacing: "-0.03em" }} data-testid="contact-h1">
              Parlons de votre projet.
            </h1>
            <p className="mt-5 text-lg text-ink-secondary max-w-[540px]">
              Le plus simple : un coup de fil. Sinon, écrivez-nous, on répond sous 24h ouvrées.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-[40fr_60fr] gap-12">
            <Reveal className="space-y-4">
              <a href={SITE.phoneHref} className="flex items-center gap-4 bg-white border border-line rounded-2xl p-6 shadow-subtle hover:shadow-elevated-royal hover:border-brand-royal transition-all" data-testid="contact-phone-card">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "var(--gradient-metal)" }}>
                  <Phone size={22} aria-hidden="true" />
                </div>
                <div>
                  <p className="text-[13px] text-ink-tertiary font-medium">Téléphone</p>
                  <p className="font-heading font-bold text-lg text-brand-deep tnum">{SITE.phone}</p>
                </div>
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 bg-white border border-line rounded-2xl p-6 shadow-subtle hover:shadow-elevated-royal hover:border-brand-royal transition-all" data-testid="contact-email-card">
                <div className="w-12 h-12 rounded-xl bg-brand-highlight flex items-center justify-center shrink-0">
                  <Mail size={22} className="text-brand-royal" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-[13px] text-ink-tertiary font-medium">Email</p>
                  <p className="font-heading font-semibold text-brand-deep break-all">{SITE.email}</p>
                </div>
              </a>
              <div className="flex items-start gap-4 bg-white border border-line rounded-2xl p-6 shadow-subtle">
                <div className="w-12 h-12 rounded-xl bg-brand-highlight flex items-center justify-center shrink-0">
                  <MapPin size={22} className="text-brand-royal" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-[13px] text-ink-tertiary font-medium">Adresse</p>
                  <p className="font-medium text-brand-deep text-[15px]">{SITE.address}</p>
                </div>
              </div>
              <div className="bg-brand-highlight rounded-2xl p-6">
                <p className="text-[15px] text-brand-deep font-medium">Besoin d'un chiffrage précis ?</p>
                <Link to="/devis" className="inline-flex items-center gap-1.5 text-brand-royal font-semibold text-[15px] mt-2 hover:underline" data-testid="contact-devis-link">
                  Utilisez le formulaire de devis <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              {sent ? (
                <div className="bg-white border border-line rounded-2xl p-10 text-center shadow-subtle" data-testid="contact-confirmation">
                  <CheckCircle2 size={56} className="text-success mx-auto" strokeWidth={1.5} />
                  <h2 className="font-heading font-bold text-2xl text-brand-deep mt-6">Message envoyé.</h2>
                  <p className="mt-3 text-ink-secondary">Merci {data.name}. Brandon vous répond sous 24h ouvrées.</p>
                </div>
              ) : (
                <div className="bg-white border border-line rounded-2xl p-8 lg:p-10 shadow-subtle space-y-5">
                  <div>
                    <label className={labelCls} htmlFor="contact-name">Nom *</label>
                    <input id="contact-name" type="text" className={inputCls} placeholder="Votre nom" value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name-input" />
                    {errors.name && <p className="text-red-600 text-[13px] mt-1.5">{errors.name}</p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls} htmlFor="contact-email">Email *</label>
                      <input id="contact-email" type="email" className={inputCls} placeholder="vous@email.fr" value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" />
                      {errors.email && <p className="text-red-600 text-[13px] mt-1.5">{errors.email}</p>}
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="contact-phone">Téléphone</label>
                      <input id="contact-phone" type="tel" className={inputCls} placeholder="06 12 34 56 78" value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone-input" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="contact-message">Message *</label>
                    <textarea id="contact-message" rows={5} maxLength={1000} className={inputCls} placeholder="Décrivez votre projet ou posez votre question…" value={data.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message-textarea" />
                    {errors.message && <p className="text-red-600 text-[13px] mt-1.5">{errors.message}</p>}
                  </div>
                  {errors.submit && <p className="text-red-600 text-sm font-medium">{errors.submit}</p>}
                  <button type="button" onClick={submit} disabled={sending} className="btn-base btn-metal w-full disabled:opacity-60" data-testid="contact-submit-btn">
                    {sending ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : null}
                    {sending ? "Envoi…" : "Envoyer le message"}
                  </button>
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
